// This module never records query strings, form values, addresses or raw URLs.
const KEY = 'web-analytics-v1';
let token = '';
let auth = '';
let page = 'other';
let basePage = 'other';
let tabId = '';
let attemptId = '';
let sequence = 0;
let lastTick = 0;
let lastVisible = true;
let nextBootstrapAt = 0;
let queue: any[] = [];
let running = false;
let enabled = false;
let init: Promise<void> | undefined;
let lastActivitySignal = 0;
let observedSession = '';
const root = () =>
  `${process.env.NEXT_PUBLIC_SERVER_URL || ''}${
    process.env.NEXT_PUBLIC_DEFAULT_ROUTE || '/api/user'
  }`.replace(/\/$/, '') + '/analytics';
const uuid = () => crypto.randomUUID();
export function analyticsPage(route: string) {
  if (route === '/') return 'home';
  if (route === '/restaurants/[category]/[restaurantId]') return 'restaurant';
  if (route === '/restaurants/[category]') return 'restaurants';
  const pages: Record<string, string> = {
    '/search': 'search',
    '/sign-in': 'sign_in',
    '/address': 'address',
    '/my-info': 'my_info',
    '/order-history': 'order_history',
    '/order-history/info/[orderId]': 'order_detail',
    '/refund': 'refund',
    '/event': 'event',
    '/promotion/share': 'promotion',
  };
  return pages[route] || 'other';
}
function session(activity: boolean) {
  const now = Date.now();
  let state: { id: string; at: number };
  try {
    state = JSON.parse(localStorage.getItem(`${KEY}:session`) || 'null');
  } catch {
    state = null as any;
  }
  if (
    !state ||
    typeof state.id !== 'string' ||
    !Number.isFinite(state.at) ||
    now - state.at >= 1800000
  ) {
    if (!activity) return state?.id || null;
    state = { id: uuid(), at: now };
    attemptId = '';
  }
  if (activity) {
    state.at = now;
    localStorage.setItem(`${KEY}:session`, JSON.stringify(state));
  }
  return state.id;
}
async function request(path: string, body: any) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 4000);
  try {
    return await fetch(root() + path, {
      method: 'POST',
      credentials: process.env.NODE_ENV === 'production' ? 'include' : 'omit',
      headers: {
        'Content-Type': 'application/json',
        ...(auth ? { Authorization: auth } : {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
      keepalive: true,
    });
  } finally {
    clearTimeout(timeout);
  }
}
async function drain() {
  if (running) return;
  running = true;
  try {
    while (queue.length) {
      const item = queue.shift();
      try {
        const result = await request('/collect', item);
        if (result.status === 409) {
          localStorage.removeItem(`${KEY}:session`);
          queue = [];
        }
        if ([401, 429, 503].includes(result.status)) {
          queue = [];
          break;
        }
      } catch {
        queue = [];
        break;
      }
    }
  } finally {
    running = false;
  }
}
export function track(
  kind: 'page_view' | 'cart_view' | 'checkout_view' | 'heartbeat' | 'activity',
) {
  try {
    if (!enabled || !token) return;
    const sessionId = session(kind !== 'heartbeat');
    if (!sessionId) return;
    if (kind === 'activity' && observedSession !== sessionId)
      kind = 'page_view';
    observedSession = sessionId;
    const now = Date.now();
    const visible = document.visibilityState === 'visible';
    const visibleMs =
      lastVisible && lastTick
        ? Math.min(30000, Math.max(0, now - lastTick))
        : 0;
    lastTick = now;
    lastVisible = visible;
    if (queue.length >= 30) return;
    queue.push({
      token,
      sessionId,
      tabId,
      eventId: uuid(),
      attemptId: attemptId || undefined,
      kind,
      page,
      device: /iPad|Tablet/i.test(navigator.userAgent)
        ? 'tablet'
        : /Mobi|Android/i.test(navigator.userAgent)
        ? 'mobile'
        : 'desktop',
      visible,
      visibleMs,
      seq: ++sequence,
    });
    void drain();
  } catch {
    /* Analytics cannot interrupt navigation or checkout. */
  }
}
export async function startAnalytics(accessToken?: string) {
  auth = accessToken || '';
  if (process.env.NEXT_PUBLIC_WEB_ANALYTICS_ENABLED !== 'true') return;
  if (init) return init;
  if (Date.now() < nextBootstrapAt) return;
  nextBootstrapAt = Date.now() + 60000;
  init = (async () => {
    try {
      const bootstrap = async () => {
        const response = await request('/bootstrap', {
          token: localStorage.getItem(`${KEY}:token`) || undefined,
        });
        if (!response.ok) throw new Error('Unavailable');
        token = (await response.json()).data.token;
        localStorage.setItem(`${KEY}:token`, token);
      };
      if (navigator.locks) await navigator.locks.request(KEY, bootstrap);
      else await bootstrap();
      tabId = uuid();
      enabled = true;
      lastTick = Date.now();
      lastVisible = document.visibilityState === 'visible';
      track('page_view');
    } catch {
      enabled = false;
      init = undefined;
    }
  })();
  return init;
}
export function analyticsHeartbeat() {
  if (!enabled) void startAnalytics(auth);
  else track('heartbeat');
}
export function setAnalyticsPage(route: string) {
  basePage = analyticsPage(route);
  if (page === basePage) return;
  page = basePage;
  track('page_view');
}
export function analyticsActivity() {
  if (Date.now() - lastActivitySignal < 15000) return;
  lastActivitySignal = Date.now();
  track('activity');
}
export function analyticsCheckout(step: string) {
  if (step !== 'cart' && step !== 'payment') return;
  try {
    session(true);
    if (!attemptId) attemptId = uuid();
  } catch {
    return;
  }
  page = step === 'cart' ? 'cart' : 'checkout';
  track(step === 'cart' ? 'cart_view' : 'checkout_view');
}
export function closeAnalyticsCheckout() {
  page = basePage;
  attemptId = '';
  track('page_view');
}
export function analyticsOrderContext() {
  try {
    if (!token || !enabled) return undefined;
    const id = session(true);
    if (!attemptId) attemptId = uuid();
    return `${token}|${id}|${tabId}|${attemptId}`;
  } catch {
    return undefined;
  }
}
